import React from "react"
import { ValueOf } from "_types/Utils"
import { ContentBlockParser } from "./ContentBlockParser"
import { FAQGroupBanner } from "components/faq/FAQGroupBanner"
import { Highlights } from "components/highlights/Highlights"
import { HtmlEmbed } from "components/html-embed/HtmlEmbed"
import { ServiceComparison } from "components/service-comparison/ServiceComparison"
import { SingleService } from "components/service-comparison/SingleService"
import { TabbedContentBlockBanner } from "components/tabbed-content-banner/TabbedContentBlockBanner"
import { MultiContentBlocks } from "components/multi-content-block/MultiContentBlocks"
import { NavigationTiles } from "components/navigation-tile/NavigationTiles"
import { CouponBanner } from "components/coupons/CouponBanner"
import { AffiliationSlider } from "components/affiliation-slider/AffiliationSlider"
import { Gallery } from "components/image-gallery/Gallery"
import { FormBuilder } from "components/forms/FormBuilder"
import { CTABanner } from "components/cta-banner/CTABanner"
import { ServiceAreaGroup } from "components/service-area-group/ServiceAreaGroup"
import { FAQPageWrapper } from "components/faq/FAQPageWrapper"

type StrapiComponentTypes = keyof typeof COMPONENTS
type StrapiComponents = ValueOf<typeof COMPONENTS>
type ContentBlocksComponents = IStrapiComponent &
  React.ComponentProps<StrapiComponents>

export type IStrapiComponent = {
  id: string
  strapi_component: StrapiComponentTypes
  alternatingContentBlocks?: boolean
  alternateBlocksCount?: number
}

type IContentBlocksComponent = IStrapiComponent & ContentBlocksComponents

interface ContentBlocksProps {
  strapiContent: IContentBlocksComponent[]
  legacyNavGridHasAltBackground?: boolean
  hasAlternatingContentBlocks?: boolean
}

const COMPONENTS = {
  "content-block.logo-slider": AffiliationSlider,
  "content-block.content-block": ContentBlockParser,
  "content-block.coupon-banner": CouponBanner,
  "content-block.cta-banner": CTABanner,
  "forms.form": FormBuilder,
  "content-block.faq-group-banner": FAQGroupBanner,
  "content-block.highlights-group": Highlights,
  "content-block.html-embed": HtmlEmbed,
  "content-block.location-group": ServiceAreaGroup,
  "content-block.navigation-block": NavigationTiles,
  "content-block.multi-content-block": MultiContentBlocks,
  "content-block.service-comparison": ServiceComparison,
  "content-block.image-gallery": Gallery,
  "content-block.service": SingleService,
  "content-block.tabbed-content-banner": TabbedContentBlockBanner,
}

export const ContentBlocks = ({
  strapiContent,
  hasAlternatingContentBlocks = true,
}: // legacyNavGridHasAltBackground = false,
ContentBlocksProps) => {
  const isFAQPage = strapiContent.some(
    ({ strapi_component }) =>
      strapi_component === "content-block.faq-group-banner"
  )

  const addIndexToComponent = (strapiComponent: `content-block.${string}`) => {
    //This function keeps track of indices for what ever component you pass to it.
    let componentCount = 0

    if (strapiContent.length > 0) {
      strapiContent.forEach(
        (component: {
          id: string
          index?: number
          strapi_component: string
        }) => {
          if (
            component.hasOwnProperty("strapi_component") &&
            Object.values(component).includes(strapiComponent)
          ) {
            componentCount++
            component.index = componentCount
          }
        }
      )
    }
  }

  addIndexToComponent("content-block.service")
  addIndexToComponent("content-block.content-block")
  let alternateBlocksCount = 0
  return (
    <>
      <FAQPageWrapper isFAQPage={isFAQPage}>
        {strapiContent.map((component, index) => {
          if (typeof COMPONENTS[component.strapi_component] !== "undefined") {
            const Component = COMPONENTS[
              component.strapi_component
            ] as React.FunctionComponent

            const alternatingContentBlocks =
              component.strapi_component === "content-block.content-block"
                ? { alternatingContentBlocks: hasAlternatingContentBlocks }
                : {}

            if (alternatingContentBlocks) {
              component.strapi_component === "content-block.content-block" &&
                (component as React.ComponentProps<typeof ContentBlockParser>)
                  ?.content_block_type !== "text-wraps-image" &&
                (component as React.ComponentProps<typeof ContentBlockParser>)
                  ?.content_block_type !== "default" &&
                alternateBlocksCount++
              component.alternateBlocksCount = alternateBlocksCount
            }
            const locationComponentParser =
              component.strapi_component === "forms.form"
                ? {
                    locationComponentParser: "content-blocks",
                  }
                : {}

            return (
              <Component
                key={`${component.id}-${index}`}
                {...component}
                {...alternatingContentBlocks}
                {...locationComponentParser}
              />
            )
          }
          return (
            <p
              key={`${component.id}-${index}`}
            >{`Component not queried yet`}</p>
          )
        })}
      </FAQPageWrapper>
    </>
  )
}
